import { useRouter } from '@/domains/shared/router/Router';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card } from '@/domains/shared/components/Card';
import { Button } from '@/domains/shared/components/Button';

const isProduction =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_APP_ENV === 'production';

export function AdminDashboardPage() {
  const { navigate } = useRouter();
  const { user, logout, leads, isLeadsLoading, leadsError, refreshLeads } = useAdminAuth();

  const totalLeads = leads.length;
  const avgScore = totalLeads
    ? Math.round(leads.reduce((acc, lead) => acc + (lead.overallScore || 0), 0) / totalLeads)
    : 0;
  const retentionRiskCount = leads.filter((l) => l.hasRetentionRisk).length;
  const qualifiedCount = leads.filter(
    (l) => l.commercialStatus === 'qualificado' || l.commercialStatus === 'convertido',
  ).length;

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      {/* Header Administrativo */}
      <header className="border-b border-white/10 bg-navy-dark/90 px-4 py-3.5 sticky top-0 z-40 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-orange text-sm font-black text-white">
              IC
            </div>
            <div>
              <span className="block text-[10px] font-extrabold uppercase tracking-wider text-brand-orange">
                DecolaCont Admin
              </span>
              <span className="block text-xs font-bold text-white sm:text-sm">
                Painel Comercial
              </span>
            </div>
          </div>

          {/* Abas e Logout */}
          <div className="flex items-center gap-3">
            <nav className="flex items-center gap-1 bg-white/5 p-1 rounded-xl border border-white/10">
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-orange text-white"
              >
                Visão Geral
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/leads')}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-offWhite/80 hover:text-white"
              >
                Leads ({totalLeads})
              </button>
            </nav>

            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await logout();
                navigate('/admin/login');
              }}
              className="text-xs text-brand-offWhite/60 hover:text-feedback-danger"
            >
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal do Dashboard */}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-brand-offWhite/80">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-feedback-success" />
            <span>
              Operador: <strong className="text-white">{user?.name || user?.email || 'Administrador'}</strong>{' '}
              {user?.email ? `(${user.email})` : ''} •{' '}
              <span className="text-feedback-success font-semibold">Admin Ativo</span>
            </span>
          </div>
          {isProduction ? (
            <span className="rounded bg-feedback-success/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-feedback-success">
              Produção Segura • Supabase RLS
            </span>
          ) : (
            <span className="rounded bg-brand-orange/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-orange">
              Ambiente Local (Mock de Interface)
            </span>
          )}
        </div>

        {leadsError && (
          <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-xs text-red-300 flex items-center justify-between">
            <span>{leadsError}</span>
            <button
              type="button"
              onClick={refreshLeads}
              className="underline font-bold hover:text-white ml-2"
            >
              Tentar novamente
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white sm:text-3xl">
              Visão Geral de Captação
            </h1>
            <p className="mt-1 text-xs text-brand-offWhite/70 sm:text-sm">
              Métricas consolidadas de maturidade e qualificação comercial.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/leads')}
            >
              Gerenciar Leads →
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/')}
            >
              Ver Site Público
            </Button>
          </div>
        </div>

        {/* 4 Cards de Métricas Principais */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card className="p-5 bg-navy-light/40">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/60">
              Total de Diagnósticos
            </span>
            <div className="mt-2 text-3xl font-black text-white">
              {isLeadsLoading && !totalLeads ? '...' : totalLeads}
            </div>
            <span className="mt-1 block text-[11px] text-feedback-success font-medium">
              100% via canal digital
            </span>
          </Card>

          <Card className="p-5 bg-navy-light/40">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/60">
              Média de Maturidade
            </span>
            <div className="mt-2 text-3xl font-black text-brand-orange">
              {isLeadsLoading && !totalLeads ? '...' : `${avgScore}%`}
            </div>
            <span className="mt-1 block text-[11px] text-brand-offWhite/60">
              Presença calculada
            </span>
          </Card>

          <Card className="p-5 bg-navy-light/40 border-feedback-danger/30">
            <span className="text-xs font-semibold uppercase tracking-wider text-feedback-danger">
              Risco de Retenção
            </span>
            <div className="mt-2 text-3xl font-black text-feedback-danger">
              {isLeadsLoading && !totalLeads ? '...' : retentionRiskCount}
            </div>
            <span className="mt-1 block text-[11px] text-brand-offWhite/60">
              Experiência do cliente &lt; 40%
            </span>
          </Card>

          <Card className="p-5 bg-navy-light/40">
            <span className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/60">
              Leads Qualificados
            </span>
            <div className="mt-2 text-3xl font-black text-feedback-success">
              {isLeadsLoading && !totalLeads ? '...' : qualifiedCount}
            </div>
            <span className="mt-1 block text-[11px] text-brand-offWhite/60">
              Status qualificado ou convertido
            </span>
          </Card>
        </div>

        {/* Resumo da Tabela de Leads Recentes */}
        <section className="mt-10" aria-labelledby="recent-leads-title">
          <div className="flex items-center justify-between mb-4">
            <h2 id="recent-leads-title" className="text-lg font-bold text-white">
              Últimos Diagnósticos Realizados
            </h2>
            <button
              type="button"
              onClick={() => navigate('/admin/leads')}
              className="text-xs text-brand-orange hover:underline font-semibold"
            >
              Ver tabela completa com filtros →
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/10 bg-navy-light/20">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-white/10 bg-white/5 text-[11px] uppercase tracking-wider text-brand-offWhite/70">
                <tr>
                  <th className="px-4 py-3.5">Nome / Contato</th>
                  <th className="px-4 py-3.5">Escritório</th>
                  <th className="px-4 py-3.5 text-center">Score Geral</th>
                  <th className="px-4 py-3.5">Gargalo Principal</th>
                  <th className="px-4 py-3.5">Status Comercial</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-brand-offWhite/60">
                      {isLeadsLoading
                        ? 'Carregando diagnósticos registrados...'
                        : 'Nenhum diagnóstico registrado até o momento.'}
                    </td>
                  </tr>
                ) : (
                  leads.slice(0, 5).map((lead) => (
                    <tr key={lead.id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3.5 font-medium text-white">
                        <div>{lead.name}</div>
                        <div className="text-[11px] text-brand-offWhite/60">{lead.phone}</div>
                      </td>
                      <td className="px-4 py-3.5 text-brand-offWhite/90">{lead.companyName}</td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-block px-2.5 py-1 rounded-md bg-brand-orange/20 font-bold text-brand-orange">
                          {lead.overallScore}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5 capitalize text-brand-offWhite/80">
                        {lead.primaryPriorityKey.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-white/10 text-white">
                          {lead.commercialStatus}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
